import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { format, startOfToday } from 'date-fns';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { EditorModule } from 'primeng/editor';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { StepperModule } from 'primeng/stepper';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, throttleTime } from 'rxjs';

import { SelectItem } from '../../../../shared/models/select-item.model';
import { YesNoPipe } from '../../../../shared/pipes/yes-no.pipe';
import { ServiceRequestConstants } from '../../service-request-constants';
import { ServiceRequest } from '../../service-request.model';
import { ServiceRequestService } from '../../service-request.service';

@Component({
  selector: 'carehub-service-request-creation',
  imports: [
    ButtonModule,
    FileUploadModule,
    StepperModule,
    ToggleSwitchModule,
    ConfirmDialogModule,
    EditorModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DatePickerModule,
    YesNoPipe,
  ],
  providers: [ConfirmationService],
  templateUrl: './service-request-creation.component.html',
  styleUrl: './service-request-creation.component.scss',
})
export class ServiceRequestCreationComponent implements OnInit {
  @Output() closeEvent = new EventEmitter<void>();

  serviceRequestForm!: FormGroup;

  currentStep: number = 1;
  types: SelectItem[] = [
    { name: 'Hardware', value: 'hardware' },
    { name: 'Software', value: 'software' },
  ];

  today = startOfToday();

  severityOptions: SelectItem[] = ServiceRequestConstants.severityOptions;
  urgencyOptions: SelectItem[] = ServiceRequestConstants.urgencyOptions;
  locationOptions: SelectItem[] = ServiceRequestConstants.locationOptions;

  serviceRequestSubject$: Subject<ServiceRequest> = new Subject<ServiceRequest>();

  constructor(
    private _router: Router,
    private _confirmationService: ConfirmationService,
    private _serviceRequestService: ServiceRequestService,
    private _formBuilder: FormBuilder
  ) {
    this.serviceRequestSubject$
      .pipe(throttleTime(2000))
      .subscribe((serviceRequest: ServiceRequest) => {
        this._serviceRequestService.create(serviceRequest).then(() => {
          this.closeEvent.emit();
          this.resetForm();
        });
      });
  }

  editorModules = {
    toolbar: ['bold', 'italic', 'strike', 'underline', { list: 'ordered' }],
  };

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.serviceRequestForm = this._formBuilder.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      requestType: [''],
      location: [null, Validators.required],
      checkedWithColleagues: [false, Validators.requiredTrue],
      priority: [''],
      critical: [''],
      severity: [null, Validators.required],
      urgency: [null, Validators.required],
      dueDate: [''],
      compliance: [''],
      confidential: [''],
      requester: [''],
      assignedTo: [''],
      files: [''],
    });
  }

  /**
   * Check if the required fields are filled in before proceeding to the next step
   *
   * @param keys string[]
   * @returns boolean
   */
  checkFieldsBeforeNextStep(keys: string[], goToStep: number): void {
    let stepState: boolean = true;
    Object.keys(this.serviceRequestForm.controls).forEach((key: string) => {
      if (keys.includes(key) && this.serviceRequestForm.controls[key].invalid) {
        stepState = false;
        // Mark the field as dirty to show the error message
        this.serviceRequestForm.controls[key].markAsDirty();
      }
    });

    // If the step is not valid, do not proceed to the next step
    if (!stepState) {
      return;
    }

    // Proceed to the next step
    this.currentStep = goToStep;
  }

  isFieldInvalid(fieldName: string): boolean {
    const control: AbstractControl | null = this.serviceRequestForm.get(fieldName);
    return !!(control?.invalid && control?.dirty);
  }

  /**
   * Converts AbstractControl to FormControl
   *
   * @param control AbstractControl | null
   * @returns FormControl
   */
  convertToFormControl(control: AbstractControl | null): FormControl {
    return control as FormControl;
  }

  /**
   * Show the value of an InputItem or number in a user-friendly way
   *
   * @param value InputItem | number | null
   * @returns string
   */
  showValue(value: SelectItem | number | null): string {
    if (typeof value === 'number') {
      return value.toString();
    }
    return value?.name || '';
  }

  /**
   * Create a new servicerequest
   */
  async createServiceRequest(): Promise<void> {
    let description: string = this.serviceRequestForm.controls['description'].value;
    description += '<br><br> ---------- <br><br>';

    const requestType: SelectItem | null = this.serviceRequestForm.controls['requestType'].value;
    if (requestType) {
      description += 'Type: ' + requestType.value + '<br>';
    }

    description += 'ServiceRequest was created on the following URL: ' + this._router.url;

    const formData: ServiceRequest = { ...this.serviceRequestForm.value };

    formData.description = description;
    formData.priority = Math.round(this.serviceRequestPriority());
    formData.severity = this.serviceRequestForm.controls['severity'].value.value;
    formData.urgency = this.serviceRequestForm.controls['urgency'].value.value;

    const dueDateControl: AbstractControl = this.serviceRequestForm.controls['dueDate'];
    const rawDueDate: string | null | undefined = dueDateControl.value;

    if (rawDueDate) {
      const formattedDate: string = format(new Date(rawDueDate), 'yyyy-MM-dd');
      formData.dueDate = formattedDate;
    } else {
      formData.dueDate = null;
    }

    this.serviceRequestForm.markAllAsTouched();
    this.serviceRequestSubject$.next(formData);
  }

  /**
   * Check if the step should be disabled based on the fields that are required
   *
   * @param fields string[]
   * @returns boolean
   */
  shouldStepBeDisabled(fields: string[]): boolean {
    return fields.some((field: string) => this.serviceRequestForm.controls[field]?.invalid);
  }

  /**
   * Confirm that the servicerequest is critical
   */
  confirmCriticalServiceRequest(): void {
    if (this.serviceRequestForm.get('critical')?.value) {
      this._confirmationService.confirm({
        header: $localize`:@@sr_creation.dialog.confirm_critical.header:Are you sure this service request is critical?`,
        message: $localize`:@@sr_creation.dialog.confirm_critical.message:If marked as critical, IT will be notified immediately and begin work. If not truly critical, it may affect the response time for other high-priority tasks.`,
        rejectButtonProps: {
          label: $localize`:@@common.button.cancel:Cancel`,
          severity: 'danger',
          outlined: true,
        },
        acceptButtonProps: {
          label: $localize`:@@common.yes:Yes`,
        },
        reject: () => {
          this.serviceRequestForm.get('critical')?.setValue(false);
        },
      });
    }
  }

  resetForm(): void {
    this.serviceRequestForm.reset({
      title: '',
      description: '',
      requestType: '',
      checkedWithColleagues: false,
      priority: '',
      critical: false,
      severity: null,
      urgency: null,
      dueDate: '',
      compliance: '',
      confidential: '',
      requester: '',
      assignedTo: '',
      files: '',
      location: null,
    });
    this.currentStep = 1;
  }

  /**
   * Close the parent dialog
   */
  closeDialog(): void {
    this.closeEvent.emit();
  }

  /**
   * Clear the file input field
   */
  onClear(): void {
    this.serviceRequestForm.get('files')?.setValue([]);
  }

  /**
   * Add selected files to the servicerequest
   *
   * @param event FileSelectEvent
   */
  onSelect(event: FileSelectEvent): void {
    this.serviceRequestForm.get('files')?.setValue(event.currentFiles);
  }

  /**
   * Get the priority of the servicerequest
   *
   * @returns number The calculated priority
   */
  serviceRequestPriority(): number {
    return this._serviceRequestService.calculatePriority(this.serviceRequestForm.value);
  }
}
